import { Button, Grid, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import React, { } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OrderDirection } from '../Datatable/OrderDirection';
import _ from 'lodash';
import CareModel from './models/CareModel';
import { CareSearchSelector, setDatatableSelectedCareIndex, setOrderBy, setOrderDirection, setTriggerCareSearch } from './reducers/CareSearchReducer';
import { userSelectorSelector } from '../UserSelector/reducers/UserSelectorReducer';
import { setCareClientName, setSelectedCareId, setSelectedClientId } from './reducers/CareInputReducer';
import Api from '../../app/apiRequest';
import Swal from 'sweetalert2';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import CareApi, { SplitCompanyParam } from './api/CareApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';

const CareTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();

    const careSearchViewData = useSelector(CareSearchSelector);
    const userSelectorData = useSelector(userSelectorSelector);

    const renderBrokersTakeCare = (brokersTakeCare: string) => {
        if (!brokersTakeCare || brokersTakeCare.length == 0) {
            return "";
        }

        let html = "";
        let brokers = brokersTakeCare.split(",");

        _.each(userSelectorData.userDisplayNames, (item) => {
            if (brokers.indexOf(item.id.toString()) > -1) {
                html += item.displayName + " - ";
            }
        });

        return html.substring(0, html.length - 3);
    }

    let renderHeader = (className: string, rowSpan: number, colSpan: number, headerName: string, allowHeaderSort: boolean, fieldName: keyof CareModel = "clientId") => {
        let headerSortClass = careSearchViewData.query.orderBy === fieldName ?
            "datatable-header-sort-wrapper-active" :
            "datatable-header-sort-wrapper";

        let tableCellHeader = allowHeaderSort ?
            "datatable-cell-header datatable-cell-sortable" :
            "datatable-cell-header";

        if (className && className.length > 0) {
            tableCellHeader += " " + className;
        }

        return <TableCell className={tableCellHeader} rowSpan={rowSpan} colSpan={colSpan}
            onClick={() => {
                if (allowHeaderSort) {
                    let sortDirection = fieldName !== careSearchViewData.query.orderBy ?
                        careSearchViewData.query.orderDirection === undefined ? OrderDirection.Asc : careSearchViewData.query.orderDirection :
                        careSearchViewData.query.orderDirection === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc;

                    dispatch(setOrderBy(fieldName.toString()));
                    dispatch(setOrderDirection(sortDirection));
                }
            }}
        >
            {headerName}
            {allowHeaderSort &&
                <span className={headerSortClass}>
                    {
                        careSearchViewData.query.orderDirection === OrderDirection.Desc &&
                        <Button
                            className={"datatable-header-sort-button"}
                        >
                            <i className="fa fa-sort-desc" aria-hidden="true"></i>
                        </Button>
                    }
                    {
                        careSearchViewData.query.orderDirection !== OrderDirection.Desc &&
                        <Button
                            className={"datatable-header-sort-button"}
                        >
                            <i className="fa fa-sort-asc" aria-hidden="true"></i>
                        </Button>
                    }
                </span>
            }
        </TableCell>;
    }

    let renderCares = () => {
        return careSearchViewData.searchResults.map((item: CareModel, index: number) => {
            return <TableRow
                className={careSearchViewData.datatableSelectedIndex == index ? "datatable-row datatable-row-selected" : "datatable-row"}
                onClick={() => {
                    dispatch(setSelectedCareId(item.careId));
                    dispatch(setSelectedClientId(item.clientId));
                    dispatch(setDatatableSelectedCareIndex(index));
                    dispatch(setCareClientName(item.clientName));
                }}
            >
                {renderCareRow(item, index)}
            </TableRow>;
        });
    }

    const splitCompany = (item: CareModel) => {
        Swal.fire({
            title: 'Are you sure want to split this company?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                CareApi.SplitCompany({
                    clientId: item.clientId,
                    year: careSearchViewData.query.year
                } as SplitCompanyParam)
                    .then((response: BaseResponse<string>) => {
                        if (response.success) {
                            Utility.Alert.Success("Split company successful");

                            dispatch(setTriggerCareSearch(new Date().getTime()));
                        }
                        else {
                            Utility.Alert.Error(response.message);
                        }

                        dispatch(showLoading(false));
                    }).catch((error) => {
                        Utility.Alert.Error(error);
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    let renderCareRow = (item: CareModel, index: number) => {
        return <>
            <TableCell>{index + 1}</TableCell>
            <TableCell>{item.clientName}</TableCell>
            <TableCell>{item.country}</TableCell>
            <TableCell>{item.city}</TableCell>
            <TableCell>{item.address}</TableCell>
            <TableCell>{item.picname}</TableCell>
            <TableCell>{item.sex == null ? "" : item.sex ? "Male" : "Female"}</TableCell>
            <TableCell>{Utility.DateFormat.formatDateString(item.picBirthday)}</TableCell>
            <TableCell>{item.phone}</TableCell>
            <TableCell>{item.hobby}</TableCell>
            <TableCell>{renderBrokersTakeCare(item.brokersTakeCare)}</TableCell>
            <TableCell>{item.comm ? (item.comm / 2).toFixed(2) : ""}</TableCell>
            <TableCell>{item.classification}</TableCell>
            <TableCell>{item.comm ? (item.comm * 0.05).toFixed() : ""}</TableCell>
            <TableCell>{item.benefit.newYear == 0 ? "" : item.benefit.newYear}</TableCell>
            <TableCell>{item.benefit.midAutumn == 0 ? "" : item.benefit.midAutumn}</TableCell>
            <TableCell>{item.benefit.companyAnniversary == 0 ? "" : item.benefit.companyAnniversary}</TableCell>
            <TableCell>{item.benefit.birthday == 0 ? "" : item.benefit.birthday}</TableCell>
            <TableCell>{item.benefit.promotion == 0 ? "" : item.benefit.promotion}</TableCell>
            <TableCell>{item.benefit.womenDay == 0 ? "" : item.benefit.womenDay}</TableCell>
            <TableCell>{item.benefit.newBaby == 0 ? "" : item.benefit.newBaby}</TableCell>
            <TableCell>{item.benefit.funeral == 0 ? "" : item.benefit.funeral}</TableCell>
            <TableCell>{item.benefit.remainForMealGift == 0 ? "" : item.benefit.remainForMealGift.toFixed(2)}</TableCell>
            <TableCell>{item.newYear?.toFixed(2)}</TableCell>
            <TableCell>{item.midAutumn?.toFixed(2)}</TableCell>
            <TableCell>{item.companyAnniversary?.toFixed(2)}</TableCell>
            <TableCell>{item.birthday?.toFixed(2)}</TableCell>
            <TableCell>{item.promotion?.toFixed(2)}</TableCell>
            <TableCell>{item.womenDay?.toFixed(2)}</TableCell>
            <TableCell>{item.newBaby?.toFixed(2)}</TableCell>
            <TableCell>{item.funeral?.toFixed(2)}</TableCell>
            <TableCell>{item.usedBudget?.toFixed(2)}</TableCell>
            <TableCell>{item.remainForMealGift?.toFixed(2)}</TableCell>
            <TableCell>
                <Tooltip title="Split Company">
                    <Button
                        className='table-action-button'
                        onClick={() => {
                            splitCompany(item);
                        }}
                        variant='outlined'
                    >Split</Button>
                </Tooltip>
            </TableCell>
        </>;
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let url = Api.constructGetUrl("/api/Care/ExportCares", careSearchViewData.query);

                        const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
                        if (newWindow) newWindow.opener = null
                    }}
                >Export Excel</Button>
            </Grid>
        </Grid>
        <div className='care-datatable-search-result nice-scroll fixture-same-line'>
            <Table className=''>
                <TableHead>
                    <TableRow className="datatable-head">
                        {renderHeader("sticky-first-header-row my-fixture-commission", 1, 1, "", false)}
                        {renderHeader("sticky-first-header-row my-fixture-commission", 1, 4, "COMPANY INFORMATION", false)}
                        {renderHeader("sticky-first-header-row my-fixture-commission", 1, 5, "PERSON IN CHARGE INFORMATION", false)}
                        {renderHeader("sticky-first-header-row my-fixture-commission", 1, 4, "ASCENT VIP CLASSIFICATION", false)}
                        {renderHeader("sticky-first-header-row my-fixture-commission", 1, 9, "BENEFIT", false)}
                        {renderHeader("sticky-first-header-row my-fixture-commission", 1, 10, "ACTUAL EXPENSES", false)}
                        {renderHeader("sticky-first-header-row my-fixture-commission", 1, 1, "", false)}
                    </TableRow>
                    <TableRow className="datatable-head">
                        {renderHeader("sticky-second-header-row", 1, 1, "NO", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "CLIENT", false, "clientName")}
                        {renderHeader("sticky-second-header-row", 1, 1, "COUNTRY", false, "country")}
                        {renderHeader("sticky-second-header-row", 1, 1, "CITY", false, "city")}
                        {renderHeader("sticky-second-header-row", 1, 1, "ADDRESS", false, "address")}
                        {renderHeader("sticky-second-header-row", 1, 1, "PERSON IN CHARGE", false, "picname")}
                        {renderHeader("sticky-second-header-row", 1, 1, "SEX", false, "sex")}
                        {renderHeader("sticky-second-header-row", 1, 1, "D.O.B", false, "picBirthday")}
                        {renderHeader("sticky-second-header-row", 1, 1, "PHONE", false, "phone")}
                        {renderHeader("sticky-second-header-row", 1, 1, "HOBBY", false, "hobby")}
                        {renderHeader("sticky-second-header-row", 1, 1, "BROKER", false, "brokersTakeCare")}
                        {renderHeader("sticky-second-header-row", 1, 1, "COMM ", false, "comm")}
                        {renderHeader("sticky-second-header-row", 1, 1, "CLASSIFICATION", false, "classification")}
                        {renderHeader("sticky-second-header-row", 1, 1, "MAX BUDGET", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "NEW YEAR", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "MID AUTUMN", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "COMPANY ANNIVERSARY", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "BIRTHDAY", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "PROMOTION", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "WOMEN DAY", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "NEW BABY", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "FUNERAL", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "REMAIN FOR MEAL/GIFT", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "NEW YEAR", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "MID AUTUMN", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "COMPANY ANNIVERSARY", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "BIRTHDAY", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "PROMOTION", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "WOMEN DAY", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "NEW BABY", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "FUNERAL", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "USED BUDGET", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "REMAIN FOR MEAL/GIFT", false)}
                        {renderHeader("sticky-second-header-row", 1, 1, "", false)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {renderCares()}
                </TableBody>
            </Table>
        </div>
    </div>
};

export default CareTable;